import type {SalaryInputUnit} from '../components/SalaryInput';

const LAKH = 100_000;
const CRORE = 10_000_000;

export const SALARY_UNIT_MULTIPLIER: Record<SalaryInputUnit, number> = {
  lakhs: LAKH,
  crores: CRORE,
};

export const SALARY_MAX_INPUT_LENGTH = 6;
export const SALARY_MIN_RUPEES = 100_000;
export const SALARY_MAX_RUPEES = 1_000_000_000;
export const SALARY_PREVIEW_SUFFIX = ' per annum';
export const SALARY_REQUIRED_ERROR = 'Please enter your annual salary';
export const SALARY_ZERO_ERROR = 'Salary must be greater than zero';
export const SALARY_MIN_ERROR = 'Annual salary must be at least ₹1 Lakh';
export const SALARY_MAX_ERROR = 'Annual salary cannot exceed ₹100 Crores';

export const SALARY_UNIT_HINT: Record<SalaryInputUnit, string> = {
  lakhs: 'Lakhs',
  crores: 'Crore',
};

export const SALARY_UNIT_PLACEHOLDER: Record<SalaryInputUnit, string> = {
  lakhs: 'Eg. 45 for 45 Lakhs per annum',
  crores: 'Eg. 4.5 for 4.5 Crores per annum',
};

export const cleanSalaryAmountInput = (
  value: string,
  allowDecimal = true
): string => {
  const numericValue = value
    .trim()
    .replace(allowDecimal ? /[^\d.]/g : /[^\d]/g, '');
  if (!allowDecimal) return numericValue.slice(0, SALARY_MAX_INPUT_LENGTH);
  const [integerPart, ...decimalParts] = numericValue.split('.');
  const normalizedDecimal = decimalParts.join('').slice(0, 2);
  const sanitizedValue = numericValue.includes('.')
    ? `${integerPart}.${normalizedDecimal}`
    : integerPart;
  return sanitizedValue.slice(0, SALARY_MAX_INPUT_LENGTH);
};

export const normalizeSalaryAmountOnBlur = (
  value: string,
  allowDecimal = true
): string => {
  const cleanValue = cleanSalaryAmountInput(value, allowDecimal);
  if (!cleanValue || cleanValue === '.') return '';
  const hasDecimal = cleanValue.includes('.');
  const [integerPart, decimalPart = ''] = cleanValue.split('.');
  const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');
  const displayInteger =
    normalizedInteger || (hasDecimal && decimalPart ? '0' : '');
  if (hasDecimal) {
    return `${displayInteger}.${decimalPart}`.replace(/\.$/, '');
  }
  return displayInteger || '0';
};

export const trimSalaryAmount = (value: number): string => {
  const truncatedValue = Math.trunc(value * 100 + 1e-9) / 100;
  return Number.isInteger(truncatedValue)
    ? String(truncatedValue)
    : String(Number(truncatedValue.toFixed(2)));
};

export const getSalaryNumber = (value: string | number): number => {
  const parsed = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getSalaryUnitForValue = (
  value: string | number
): SalaryInputUnit => {
  const numericValue = getSalaryNumber(value);
  if (!numericValue) return 'lakhs';
  return numericValue >= SALARY_UNIT_MULTIPLIER.crores ? 'crores' : 'lakhs';
};

export const getSalaryInputDisplayValue = (
  value: string | number,
  unit: SalaryInputUnit
): string => {
  const valueString = String(value ?? '').trim();
  const numericValue = getSalaryNumber(value);
  if (!valueString) return '';
  if (!numericValue) return '0';
  const salaryAmount = numericValue / SALARY_UNIT_MULTIPLIER[unit];
  if (
    numericValue > 0 &&
    numericValue < SALARY_MIN_RUPEES &&
    unit === 'lakhs'
  ) {
    const truncatedValue = Math.floor(salaryAmount * 100) / 100;
    return truncatedValue.toFixed(2);
  }
  return trimSalaryAmount(salaryAmount);
};

export const getStoredSalaryValue = (
  displayValue: string,
  unit: SalaryInputUnit
): string => {
  if (!displayValue || displayValue === '.') return '';
  const numericValue = Number(displayValue);
  if (!Number.isFinite(numericValue)) return '';
  return String(Math.round(numericValue * SALARY_UNIT_MULTIPLIER[unit]));
};

export const getSalaryRupeesValue = (
  displayValue: string,
  unit: SalaryInputUnit
): number => {
  const storedValue = getStoredSalaryValue(displayValue, unit);
  return storedValue ? Number(storedValue) : 0;
};

export const getSalaryPreviewText = (value: string | number): string => {
  const rupeeValue = getSalaryNumber(value);
  if (!rupeeValue) return '';
  const previewUnit: SalaryInputUnit =
    rupeeValue >= SALARY_UNIT_MULTIPLIER.crores ? 'crores' : 'lakhs';
  const displayValue = trimSalaryAmount(
    rupeeValue / SALARY_UNIT_MULTIPLIER[previewUnit]
  );
  const isSingular = Number(displayValue) === 1;
  const unitLabel = previewUnit === 'crores'
    ? isSingular ? 'crore' : 'crores'
    : isSingular ? 'lakh' : 'lakhs';
  return `${displayValue} ${unitLabel} rupees${SALARY_PREVIEW_SUFFIX}`;
};

export const getSalaryValidationMessage = (
  displayValue: string,
  unit: SalaryInputUnit,
  submitCount: number,
  requiredError = SALARY_REQUIRED_ERROR,
  storedValue?: string | number,
  enforceMin = true,
  zeroError = SALARY_ZERO_ERROR,
  maxError = SALARY_MAX_ERROR
): string => {
  const trimmedValue = displayValue.trim();
  if (!trimmedValue || trimmedValue === '.') {
    return submitCount > 0 ? requiredError : '';
  }
  const numericValue = Number(trimmedValue);
  if (!Number.isFinite(numericValue)) return '';
  const storedValueString = String(storedValue ?? '').trim();
  const storedRupeeValue = storedValueString
    ? getSalaryNumber(storedValueString)
    : 0;
  const rupeeValue =
    storedRupeeValue > 0 && storedRupeeValue < SALARY_MIN_RUPEES
      ? storedRupeeValue
      : getSalaryRupeesValue(trimmedValue, unit);
  if (enforceMin && rupeeValue > 0 && rupeeValue < SALARY_MIN_RUPEES)
    return SALARY_MIN_ERROR;
  if (numericValue === 0 || rupeeValue === 0) return zeroError;
  if (rupeeValue > SALARY_MAX_RUPEES) return maxError;
  return '';
};

export const getSalaryPasteHint = (unit: SalaryInputUnit): string =>
  `Enter your salary in ${SALARY_UNIT_HINT[unit]}`;

export const getSalaryPlaceholder = (unit: SalaryInputUnit): string =>
  SALARY_UNIT_PLACEHOLDER[unit];

/** "₹12 Lakhs" / "₹1 Crore", singular/plural aware. */
export const formatSalaryAmount = (salaryInRupees: number): string => {
  if (!salaryInRupees) return 'not set';
  const unit: SalaryInputUnit =
    salaryInRupees >= SALARY_UNIT_MULTIPLIER.crores ? 'crores' : 'lakhs';
  const amount = trimSalaryAmount(
    salaryInRupees / SALARY_UNIT_MULTIPLIER[unit]
  );
  const singular = Number(amount) === 1;
  const unitLabel =
    unit === 'crores'
      ? singular
        ? 'Crore'
        : 'Crores'
      : singular
        ? 'Lakh'
        : 'Lakhs';
  return `₹${amount} ${unitLabel}`;
};

/** "₹12 Lakhs per annum" — the same amount where the sentence needs the period spelled
 * out. Where the surrounding copy already says "annual", use `formatSalaryAmount`. */
export const formatAnnualSalary = (salaryInRupees: number): string => {
  const amount = formatSalaryAmount(salaryInRupees);
  return amount === 'not set' ? amount : `${amount} per annum`;
};
